const SUMMARY_URL = 'https://smart-widget-assets.ekomiapps.de/data_files/json/910_seller_summary.json'
const FALLBACK_PROFILE = 'https://www.ekomi.de/bewertungen-zaun24.de.html'
const CACHE_MS = 60 * 60 * 1000

export interface EkomiSummary {
  rating: string
  totalReviews: number
  profile: string
}

let cached: EkomiSummary | null = null
let cachedAt = 0
let inflight: Promise<EkomiSummary | null> | null = null

function parseSummary (data): EkomiSummary | null {
  const rating = Number(data && data.rating)
  const totalReviews = Number(data && data.total_reviews)
  if (!Number.isFinite(rating) || rating < 1 || rating > 5) return null
  if (!Number.isInteger(totalReviews) || totalReviews < 1) return null
  const rawProfile = data && data.ekomi_certificate_seo
  const profile = typeof rawProfile === 'string' && rawProfile.startsWith('https://www.ekomi.de/')
    ? rawProfile
    : FALLBACK_PROFILE
  return {
    rating: rating.toFixed(1).replace(/\.0$/, ''),
    totalReviews,
    profile
  }
}

function fetchWithTimeout (url: string, ms: number): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ms)
  return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(timer))
}

export function loadEkomiSummary (): Promise<EkomiSummary | null> {
  if (cached && Date.now() - cachedAt < CACHE_MS) return Promise.resolve(cached)
  if (inflight) return inflight
  inflight = fetchWithTimeout(SUMMARY_URL, 2500)
    .then(response => response.ok ? response.json() : null)
    .then(data => {
      const parsed = data ? parseSummary(data) : null
      if (parsed) {
        cached = parsed
        cachedAt = Date.now()
      }
      return cached
    })
    .catch(() => cached)
    .finally(() => {
      inflight = null
    })
  return inflight
}

export function ekomiJsonLd (summary: EkomiSummary | null, shop: { name: string, url: string }) {
  if (!summary || !shop || !shop.url) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'OnlineStore',
    name: shop.name || 'ZAUN24',
    url: shop.url,
    sameAs: [summary.profile],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: summary.rating,
      bestRating: '5',
      worstRating: '1',
      ratingCount: summary.totalReviews,
      reviewCount: summary.totalReviews
    }
  }
}

export const ekomiStore = {
  namespaced: true,
  state: {
    summary: null as EkomiSummary | null
  },
  mutations: {
    setSummary (state, summary: EkomiSummary | null) {
      state.summary = summary
    }
  },
  actions: {
    async load ({ commit }) {
      const summary = await loadEkomiSummary()
      if (summary) commit('setSummary', summary)
    }
  }
}
